import { Report } from '@/api/generatedApi';
import { BaseContextMenu } from '@/components/ContextMenu/BaseContextMenu';
import { CopyMenuItem } from '@/components/ContextMenu/CopyMenuItem';
import { DataGridLazy } from '@/components/DataGrid/DataGridLazy';
import { reportColumns } from '@/components/Report/columnDefinitions';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useContextMenu } from '@/hooks/useContextMenu';
import { Box, IconButton, ListItemIcon, ListItemText } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { StyledListItem } from '@/components/AssetsPage/PopoverList';

export const ReportsTable = ({
  fetchRows,
  onDeleteClick,
}: {
  fetchRows: (parameters: {
    page: number;
    perPage: number;
  }) => Promise<{ reports: Report[] } & { pageData: { total: number } }>;
  onDeleteClick: (reports: Report[]) => void;
}) => {
  const gridApi = useGridApiRef();

  const { selectedRows, selectionModel, isLoadingAll, handleSelectionChange } = useBulkSelection<
    Report,
    { reports: Report[] }
  >({
    fetchContents: fetchRows,
    dataKey: 'reports',
    gridApi,
  });

  const {
    contextMenuPosition,
    contextMenuClickedItem,
    contextMenuClickedField,
    handleGridContextMenu,
    handleCloseContextMenu,
  } = useContextMenu<Report>({
    canUseContextMenu: true,
    getRowById: (rowId: string) => {
      const row = gridApi.current?.getRow(rowId);
      return row ?? undefined;
    },
  });

  const copyValue =
    contextMenuClickedItem && contextMenuClickedField
      ? reportColumns.find(col => col.field === contextMenuClickedField)?.getCopyValue?.(contextMenuClickedItem)
      : undefined;
  const hasCopyValue = !!copyValue;

  const handleDeleteFromContextMenu = () => {
    if (contextMenuClickedItem) {
      onDeleteClick([contextMenuClickedItem]);
      handleCloseContextMenu();
    }
  };

  const handleBulkDelete = () => {
    onDeleteClick(selectedRows);
  };

  return (
    <>
      <Box sx={{ flex: 1, width: '100%', minHeight: 0, display: 'flex', gap: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <DataGridLazy
            columns={reportColumns}
            fetchRows={fetchRows}
            dataKey='reports'
            disableFilter={false}
            checkboxSelection
            apiRef={gridApi}
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={handleSelectionChange}
            disableRowSelectionOnClick
            checkboxSelectionVisibleOnly={false}
            rowHeight={60}
            initialState={{
              pinnedColumns: { right: ['__check__'] },
            }}
            slotProps={{
              row: {
                onContextMenu: handleGridContextMenu,
              },
            }}
            sx={{
              '& .MuiDataGrid-cell.details-cell': {
                lineHeight: 'normal',
                overflow: 'visible',
              },
            }}
          />
        </Box>
        {selectedRows.length > 0 && (
          <Box
            sx={{
              width: 60,
              backgroundColor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: 1,
              gap: 1,
              flexShrink: 0,
            }}
          >
            <IconButton
              color='error'
              onClick={handleBulkDelete}
              disabled={isLoadingAll}
              title={`Delete ${selectedRows.length} report${selectedRows.length > 1 ? 's' : ''}`}
              sx={{ width: 40, height: 40 }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        )}
      </Box>
      {(hasCopyValue || contextMenuClickedItem) && (
        <BaseContextMenu anchorPosition={contextMenuPosition} onClose={handleCloseContextMenu}>
          {hasCopyValue && (
            <CopyMenuItem
              columns={reportColumns}
              row={contextMenuClickedItem}
              field={contextMenuClickedField}
              onCopy={handleCloseContextMenu}
            />
          )}
          {contextMenuClickedItem && (
            <StyledListItem onClick={handleDeleteFromContextMenu}>
              <ListItemIcon>
                <DeleteIcon fontSize='small' color='error' />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </StyledListItem>
          )}
        </BaseContextMenu>
      )}
    </>
  );
};
